import { z } from 'zod';
import type { GroupeBareme } from './familles.js';

/** Axes de notation de l'entretien présentiel (docs/BAREME.md § Axes). */
export const AXES = [
  'experience',
  'technique',
  'encadrement',
  'fiabilite',
  'disponibilite',
  'relation',
  'conformite',
  'alcool',
  'tabac',
] as const;
export type Axe = (typeof AXES)[number];

export const LIBELLES_AXES: Record<Axe, string> = {
  experience: 'Expérience du métier et du secteur',
  technique: 'Compétences techniques du poste',
  encadrement: 'Encadrement et autonomie',
  fiabilite: 'Fiabilité du parcours',
  disponibilite: 'Disponibilité et logistique',
  relation: 'Relation client et présentation',
  conformite: 'Rigueur et conformité documentaire',
  alcool: 'Tolérance zéro alcool',
  tabac: 'Tenue de poste sans interruption',
};

export type Poids = Record<Axe, number>;
export type Scores = Partial<Record<Axe, number>>;

export const poidsSchema = z.object(
  Object.fromEntries(AXES.map((a) => [a, z.number().min(0).max(100)])) as Record<
    Axe,
    z.ZodNumber
  >,
);

export const scoresSchema = z
  .object(
    Object.fromEntries(AXES.map((a) => [a, z.number().int().min(0).max(5).optional()])) as Record<
      Axe,
      z.ZodOptional<z.ZodNumber>
    >,
  )
  .strict();

/**
 * Poids par défaut, repris ligne pour ligne du tableau de docs/BAREME.md.
 *
 * ATTENTION — ces lignes ne totalisent pas toutes 100 : seul `magasin` y arrive,
 * les cinq autres totalisent 105. La note est donc divisée par la somme réelle
 * des poids (voir `calculerNote`) et non par 100 : la pondération relative du
 * tableau est respectée, et la note reste bornée à 5 comme l'exige la contrainte
 * `note_entretien BETWEEN 0 AND 5` du schéma. À arbitrer avec le responsable RH :
 * corriger le tableau, ou entériner cette normalisation.
 */
export const POIDS_DEFAUT: Record<GroupeBareme, Poids> = {
  chauffeur:     { experience: 15, technique: 12, encadrement: 5,  fiabilite: 8,  disponibilite: 8,  relation: 4,  conformite: 3,  alcool: 25, tabac: 25 },
  magasin:       { experience: 20, technique: 20, encadrement: 5,  fiabilite: 10, disponibilite: 12, relation: 5,  conformite: 8,  alcool: 10, tabac: 10 },
  production:    { experience: 20, technique: 20, encadrement: 5,  fiabilite: 10, disponibilite: 12, relation: 5,  conformite: 3,  alcool: 5,  tabac: 25 },
  vente:         { experience: 18, technique: 18, encadrement: 5,  fiabilite: 8,  disponibilite: 14, relation: 10, conformite: 2,  alcool: 5,  tabac: 25 },
  commercial:    { experience: 25, technique: 20, encadrement: 10, fiabilite: 10, disponibilite: 15, relation: 10, conformite: 5,  alcool: 5,  tabac: 5 },
  administratif: { experience: 25, technique: 25, encadrement: 10, fiabilite: 15, disponibilite: 10, relation: 5,  conformite: 10, alcool: 0,  tabac: 5 },
};

/**
 * Critères éliminatoires (docs/BAREME.md § 3) : indépendants des poids, ils
 * forcent la note à 0 et le statut à « écarté », motif obligatoire.
 */
export const CRITERES_ELIMINATOIRES = [
  { cle: 'horaires',       libelle: "Indisponibilité pour les horaires du poste" },
  { cle: 'justificatifs',  libelle: 'Refus de fournir les justificatifs' },
  { cle: 'incoherence',    libelle: 'Incohérence majeure entre le CV et les réponses' },
  { cle: 'reference',      libelle: 'Refus de communiquer une référence vérifiable' },
  { cle: 'hygiene',        libelle: "Refus des règles d'hygiène ou de tolérance zéro" },
  { cle: 'permis',         libelle: 'Permis non valide (familles chauffeur)' },
] as const;

export function sommePoids(poids: Poids): number {
  return AXES.reduce((total, axe) => total + poids[axe], 0);
}

/**
 * note = Σ (score_axe × poids_axe) / Σ (poids), arrondi au dixième.
 * Un axe sans score ne pèse pas : son poids sort du dénominateur, sinon une
 * trame incomplète ferait chuter la note d'un candidat qu'on n'a pas interrogé
 * sur tous les axes.
 */
export function calculerNote(scores: Scores, poids: Poids): number {
  let numerateur = 0;
  let denominateur = 0;
  for (const axe of AXES) {
    const score = scores[axe];
    if (score === undefined) continue;
    numerateur += score * poids[axe];
    denominateur += poids[axe];
  }
  if (denominateur === 0) return 0;
  return Math.round((numerateur / denominateur) * 10) / 10;
}

/**
 * Modification d'un poids par le super admin : les autres axes sont
 * réajustés proportionnellement pour que le total retombe à 100
 * (docs/BAREME.md § Poids par défaut).
 */
export function reequilibrer(poids: Poids, axe: Axe, valeur: number): Poids {
  const cible = Math.min(100, Math.max(0, valeur));
  const resteActuel = sommePoids(poids) - poids[axe];
  const resteVoulu = 100 - cible;
  const suivant = { ...poids, [axe]: cible } as Poids;

  if (resteActuel === 0) {
    // Tous les autres axes sont à zéro : on répartit uniformément.
    const autres = AXES.filter((a) => a !== axe);
    const part = resteVoulu / autres.length;
    for (const a of autres) suivant[a] = arrondiDixieme(part);
  } else {
    for (const a of AXES) {
      if (a === axe) continue;
      suivant[a] = arrondiDixieme((poids[a] * resteVoulu) / resteActuel);
    }
  }

  // L'arrondi peut laisser un écart de quelques dixièmes : il est absorbé par
  // l'axe le plus lourd parmi les autres, pour que le total fasse exactement 100.
  const ecart = arrondiDixieme(100 - sommePoids(suivant));
  if (ecart !== 0) {
    const plusLourd = AXES.filter((a) => a !== axe).sort((x, y) => suivant[y] - suivant[x])[0];
    if (plusLourd) suivant[plusLourd] = arrondiDixieme(suivant[plusLourd] + ecart);
  }
  return suivant;
}

function arrondiDixieme(valeur: number): number {
  return Math.round(valeur * 10) / 10;
}
